import {
  ApiListResponseBase,
  ApiResponseBase,
  DateTimeRange,
  EntityResponseBase,
  PaginatedQueryBase,
} from '@/models/common.models';
import { WorkflowTrigger } from '@/api';

export enum OrganizationMembershipRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  MEMBER = 'member',
}

export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export enum UserFlag {
  NEEDS_LOGOUT = 'needs_logout',
  SESSION_REPLY_ENABLED = 'session_reply_enabled',
}

export enum MFAMethod {
  NO_MFA = 'no_mfa',
  SMS = 'sms',
  TOTP = 'totp',
}

export enum DateFormat {
  EN_GB = 'en-GB',
  EN_US = 'en-US',
}

export interface OrganizationBase {
  avatarUrl: string;
  id: string;
  label: string;
  name: string;
}

export interface UserBase {
  avatarUrl: string;
  id: string;
  username: string;
  name: string;
  email: string;
  status: UserStatus;
  timezone: string;
  mfaMethod: MFAMethod;
  passwordLastUpdated: string;
  phoneNumber: string;
}

export interface ProgramBase {
  id: string;
  label: string;
  name: string;
  organizationId: string;
  archived?: boolean;
}

export enum UserProviderType {
  GITHUB = 'github',
}

export interface UserProviderData {
  userId: string;
  username: string;
  updatedAt: string;
  emailVerified: boolean;
  email: string;
  name: string;
  avatarUrl: string;
  accessToken: string;
}

export enum OrganizationMembershipStatus {
  ACTIVE = 'active',
  IMPORTED = 'imported',
  INACTIVE = 'inactive',
  INVITED = 'invited',
  PENDING = 'pending',
}

export type UserProviderResponse = EntityResponseBase & {
  userId: string;
  provider: UserProviderType;
  data: UserProviderData;
};

export enum NotificationActivityType {
  DUE_DATE_APPROACHING = 'dueDateApproaching',
  DUE_DATE_ARRIVED = 'dueDateArrived',
  OVERDUE = 'overdue',
  CREATED_ARCHIVED_UNARCHIVED = 'createdDeleted',
  PARENT_MODIFIED = 'parentModified',
  CUSTOM_FIELD_MODIFIED = 'customFieldModified',
  LABELS_MODIFIED = 'labelsModified',
  TEAM_MODIFIED = 'teamModified',
  ASSIGNEE_MODIFIED = 'assigneeModified',
  ESTIMATE_MODIFIED = 'estimateModified',
  POINT_EFFORT_MODIFIED = 'pointEffortModified',
  PARTICIPANTS_MODIFIED = 'participantsModified',
  TIMERS_MODIFIED = 'timersModified',
  BOARD_MODIFIED = 'boardModified',
  LANE_MODIFIED = 'laneModified',
  RELATIONSHIPS_MODIFIED = 'relationshipsModified',
  DESCRIPTION_MODIFIED = 'descriptionModified',
  COMMENTS_MODIFIED = 'commentsModified',
  ASSETS_MODIFIED = 'assetsModified',
  PULL_REQUEST_MODIFIED = 'pullRequestModified',
  TITLE_MODIFIED = 'titleModified',
  START_DATE_MODIFIED = 'startDateModified',
  DUE_DATE_MODIFIED = 'dueDateModified',
  TIME_SPENT_SECONDS_MODIFIED = 'timeSpentSecondsModified',
  PRIORITY_MODIFIED = 'priorityModified',
  SUB_ITEM_CREATED_DELETED = 'subItemCreatedDeleted',
  SUB_ITEM_ASSIGNEE_MODIFIED = 'subItemAssigneeModified',
  SUB_ITEM_TITLE_MODIFIED = 'subItemTitleModified',
  SUB_ITEM_START_DATE_MODIFIED = 'subItemStartDateModified',
  SUB_ITEM_DUE_DATE_MODIFIED = 'subItemDueDateModified',
  SUB_ITEM_COMPLETED_MODIFIED = 'subItemCompletedModified',
  WORK_TYPE_MODIFIED = 'workTypeModified',
}

export const notificationTypeMapper = {
  [NotificationActivityType.DUE_DATE_APPROACHING]: 'Due date approaching',
  [NotificationActivityType.DUE_DATE_ARRIVED]: 'Due date arrived',
  [NotificationActivityType.OVERDUE]: 'Overdue',
  [NotificationActivityType.CREATED_ARCHIVED_UNARCHIVED]:
    'Item created, archived or unarchived',
  [NotificationActivityType.PARENT_MODIFIED]: 'Parent modified',
  [NotificationActivityType.CUSTOM_FIELD_MODIFIED]: 'Custom fields modified',
  [NotificationActivityType.LABELS_MODIFIED]: 'Tags modified',
  [NotificationActivityType.TEAM_MODIFIED]: 'Team modified',
  [NotificationActivityType.ASSIGNEE_MODIFIED]: 'Assignee modified',
  [NotificationActivityType.ESTIMATE_MODIFIED]: 'Time estimate modified',
  [NotificationActivityType.POINT_EFFORT_MODIFIED]: 'Point effort modified',
  [NotificationActivityType.PARTICIPANTS_MODIFIED]: 'Participants modified',
  [NotificationActivityType.TIMERS_MODIFIED]: 'Timers modified',
  [NotificationActivityType.BOARD_MODIFIED]: 'Cycle/Board modified',
  [NotificationActivityType.LANE_MODIFIED]: 'Status modified',
  [NotificationActivityType.RELATIONSHIPS_MODIFIED]: 'Relationships modified',
  [NotificationActivityType.DESCRIPTION_MODIFIED]: 'Description modified',
  [NotificationActivityType.COMMENTS_MODIFIED]: 'Comments modified',
  [NotificationActivityType.ASSETS_MODIFIED]: 'Assets modified',
  [NotificationActivityType.PULL_REQUEST_MODIFIED]: 'Pull request activity',
  [NotificationActivityType.TITLE_MODIFIED]: 'Title modified',
  [NotificationActivityType.START_DATE_MODIFIED]: 'Start date modified',
  [NotificationActivityType.DUE_DATE_MODIFIED]: 'Due date modified',
  [NotificationActivityType.TIME_SPENT_SECONDS_MODIFIED]: 'Time spent modified',
  [NotificationActivityType.PRIORITY_MODIFIED]: 'Priority modified',
  [NotificationActivityType.SUB_ITEM_CREATED_DELETED]:
    'Subtask created or deleted',
  [NotificationActivityType.SUB_ITEM_ASSIGNEE_MODIFIED]:
    'Subtask assignee modified',
  [NotificationActivityType.SUB_ITEM_TITLE_MODIFIED]: 'Subtask modified',
  [NotificationActivityType.SUB_ITEM_START_DATE_MODIFIED]:
    'Subtask start date modified',
  [NotificationActivityType.SUB_ITEM_DUE_DATE_MODIFIED]:
    'Subtask due date modified',
  [NotificationActivityType.SUB_ITEM_COMPLETED_MODIFIED]:
    'Subtask completed modified',
  [NotificationActivityType.WORK_TYPE_MODIFIED]: 'Item type modified',
};

export enum NotificationType {
  NOTIFICATION = 'notification',
  WORKFLOW = 'workflow',
}

export type RoleNotificationConfig = {
  assignee: boolean;
  creator: boolean;
  watcher: boolean;
  participant: boolean;
};

export type WorkflowData = {
  trigger: WorkflowTrigger;
  inputParams: Record<string, unknown>;
};

export type WorkItemNotificationActivity = {
  type: NotificationActivityType;
  notificationType: NotificationType;
  roles: RoleNotificationConfig;
  workflowData?: WorkflowData;
};

export type NotificationConfig = {
  workItem: Array<WorkItemNotificationActivity>;
};

export type MentionNotificationConfig = {
  inApp: boolean;
  slack: boolean;
  email: boolean;
};

export enum MentionChannelOptions {
  inApp = 'DevStride Notification Center',
  slack = 'Slack',
  email = 'Email',
}

export type UserNotificationConfig = {
  mentionConfig: MentionNotificationConfig;
  inAppConfig: NotificationConfig;
  emailConfig: NotificationConfig;
  slackConfig: NotificationConfig;
};

export type OrganizationMembership = EntityResponseBase & {
  userId: string;
  organizationId: string;
  organization: OrganizationBase;
  status: OrganizationMembershipStatus;
  role: OrganizationMembershipRole;
  labels: Array<string>;
  user: UserBase;
  expires?: number;
  teams: Array<string>;
  range: DateTimeRange;
  joinedDate?: string;
  reinstatedDate?: string;
  deactivationDate?: string;
};

export type OrganizationMembershipQuery = Omit<
  PaginatedQueryBase,
  'limit' | 'cursor'
> & {
  q?: string;
  label?: Array<string>;
  role?: Array<OrganizationMembershipRole>;
  status?: Array<OrganizationMembershipStatus>;
  sort?:
    | 'name'
    | 'email'
    | 'joinedDate'
    | 'reinstatedDate'
    | 'deactivationDate';
  teamId?: Array<string>;
  userId?: Array<string>;
};

export type ProgramMembership = EntityResponseBase & {
  userId: string;
  organizationId: string;
  program: ProgramBase;
  status: OrganizationMembershipStatus;
  role: OrganizationMembershipRole;
};

export type Team = EntityResponseBase & {
  organizationId: string;
  name: string;
  members: Array<OrganizationMembership>;
  archived: boolean;
  laneCollectionId: string;
};

export type User = EntityResponseBase & {
  username: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  isEmailVerified: boolean;
  phoneNumber?: string;
  isPhoneNumberVerified?: boolean;
  avatarUrl: string;
  status: UserStatus;
  flags: Array<UserFlag>;
  providers: Array<UserProviderResponse>;
  organizationMemberships: Array<OrganizationMembership>;
  programMemberships: Array<ProgramMembership>;
  mfaMethod: MFAMethod;
  passwordLastUpdated: string;
  timezone: string;
  dateFormat: DateFormat;
  notificationConfig: UserNotificationConfig;
  title?: string;
  jobRoles?: Array<string>;
  createOrganizationName?: string;
};

export type UpdateUser = {
  username?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  timezone?: string;
  dateFormat?: DateFormat;
  avatarUrl?: string;
  notificationConfig?: UserNotificationConfig;
  title?: string;
  jobRoles?: Array<string>;
};

export type CreateUser = {
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl: string;
  password: string;
  timezone: string;
  dateFormat: string;
  phoneNumber?: string;
  title?: string;
  jobRoles?: Array<string>;
  createOrganizationName?: string;
};

export type ConfirmUserSignup = {
  username: string;
  code: string;
};
export type SignIn = {
  username: string;
  password: string;
};

export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  idToken: string;
}

export interface ChallengeResponse {
  challengeName: MFAMethod;
  session: string;
  username: string;
}

export interface ConfirmSignIn {
  username: string;
  session: string;
  code: string;
}

export interface SetPreferredMfa {
  mfaMethod: MFAMethod;
  code?: string;
}

export type RefreshTokens = {
  refreshToken: string;
};

export type VerifyPhoneNumber = {
  code: string;
};
export type VerifyEmail = {
  code: string;
};

export type SendForgotPasswordCode = {
  username: string;
};

export type ConfirmNewPassword = {
  username: string;
  code: string;
  newPassword: string;
};

export type UserAuthResponse = {
  user?: User;
  token?: TokenResponse;
  challenge?: ChallengeResponse;
};

export type BulkInviteUsers = {
  emails: Array<string>;
};

export type UpdateOrganizationMembership = {
  role?: OrganizationMembershipRole;
  labels?: Array<string>;
  status?: OrganizationMembershipStatus;
};

export type CreateTeam = {
  name: string;
  members?: Array<string>;
};

export type UpdateTeam = {
  name?: string;
  members?: Array<string>;
};

export type FindTeamsQueryParams = {
  q?: string;
  estimateOptionCollectionId?: string;
  archived?: boolean;
};

export type CreateApiKeyResponse = ApiResponseBase<
  ApiKey & {
    apiSecret: string;
  }
>;
export type CreateApiKey = {
  label: string;
};

export type ApiKey = EntityResponseBase & {
  organizationId: string;
  userId: string;
  label: string;
  apiKey: string;
};

export type ApiKeyResponse = ApiResponseBase<ApiKey>;
export type UserResponse = ApiResponseBase<User>;
export type UserListResponse = ApiListResponseBase<User>;
export type TeamResponse = ApiResponseBase<Team>;
export type TeamListResponse = ApiListResponseBase<Team>;
export type FindUserApiKeysResponse = ApiListResponseBase<ApiKey>;
export type OrganizationMembershipResponse =
  ApiResponseBase<OrganizationMembership>;
export type OrganizationMembershipListResponse =
  ApiListResponseBase<OrganizationMembership>;
